import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, ShoppingBag, ShoppingCart, Minus, Copy, Send, Package, X, MessageCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fileUrl, brl, waLink, firstName } from "../lib/api";
import { copyText } from "../lib/hooks";
import { Avatar, Empty, Field, Loader, PageHeader, PillTabs, StatusPill } from "../components/common";
import { ImageUpload } from "../components/ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

const CATS = ["Uniformes", "Bonés", "Pulseiras", "Acessórios"];
const ORDER_STATUS = { pendente: "Aguardando PIX", aguardando: "Pagamento informado", pago: "Pago", entregue: "Entregue", cancelado: "Cancelado" };
const ORDER_PILL = { pendente: "pendente", aguardando: "aguardando", pago: "pago", entregue: "pago", cancelado: "atrasado" };

const ProductForm = ({ open, onOpenChange, product, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF(product ? { ...product, sizesText: product.sizes.join(", "), stock: product.stock ?? "" }
      : { name: "", description: "", category: "Uniformes", price: "", images: [], sizesText: "P, M, G, GG", stock: "", active: true });
  }, [open, product]);
  const save = async (e) => {
    e.preventDefault();
    const body = { name: f.name, description: f.description, category: f.category, price: Number(f.price || 0), images: f.images.filter(Boolean),
      sizes: f.sizesText.split(",").map((s) => s.trim()).filter(Boolean), stock: f.stock === "" ? null : Number(f.stock), active: f.active };
    try {
      if (product) await api.put(`/products/${product.id}`, body); else await api.post("/products", body);
      toast.success("Produto salvo"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (!f.images) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-lg">
        <DialogHeader><DialogTitle className="h-display text-3xl">{product ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Fotos (até 4)">
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2, 3].map((i) => (
                <ImageUpload key={i} value={f.images[i] || null} testid={`product-image-${i}`} label="Foto"
                  onChange={(v) => { const imgs = [...f.images]; imgs[i] = v; setF({ ...f, images: imgs }); }} />
              ))}
            </div>
          </Field>
          <Field label="Nome"><input required data-testid="product-name-input" className="inp" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Categoria">
              <select data-testid="product-category-select" className="inp" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
            </Field>
            <Field label="Preço (R$)"><input required type="number" step="0.01" data-testid="product-price-input" className="inp" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tamanhos (vírgula)"><input data-testid="product-sizes-input" className="inp" value={f.sizesText} onChange={(e) => setF({ ...f, sizesText: e.target.value })} placeholder="Vazio = tamanho único" /></Field>
            <Field label="Estoque"><input type="number" data-testid="product-stock-input" className="inp" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} placeholder="Ilimitado" /></Field>
          </div>
          <Field label="Descrição"><textarea rows={3} data-testid="product-description-input" className="inp" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="product-active-checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />Visível na loja</label>
          <button data-testid="product-save-btn" className="btn-lime w-full rounded-full py-3">Salvar produto</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ProductDialog = ({ product, onClose, onAdd }) => {
  const [img, setImg] = useState(0);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  useEffect(() => { setImg(0); setSize(""); setQty(1); }, [product]);
  if (!product) return null;
  const add = () => {
    if (product.sizes.length && !size) return toast.error("Escolha o tamanho");
    onAdd({ product_id: product.id, name: product.name, price: product.price, image: product.images[0], size, qty });
    onClose();
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-2xl">
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <div className="aspect-square overflow-hidden rounded-xl bg-[#0B0E14]">
              {product.images[img] ? <img src={fileUrl(product.images[img])} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Package className="h-12 w-12 text-[#232B3E]" /></div>}
            </div>
            {product.images.length > 1 && (
              <div className="mt-2 flex gap-2">
                {product.images.map((p, i) => <button key={p} onClick={() => setImg(i)} className={`h-14 w-14 overflow-hidden rounded-lg border ${i === img ? "border-[#E4F222]" : "border-[#232B3E]"}`}><img src={fileUrl(p)} alt="" className="h-full w-full object-cover" /></button>)}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div><div className="eyebrow">{product.category}</div><div className="h-display text-3xl">{product.name}</div><div className="font-display text-3xl font-bold text-[#E4F222]">{brl(product.price)}</div></div>
            {product.description && <p className="text-sm text-[#94A3B8]">{product.description}</p>}
            {product.sizes.length > 0 && (
              <Field label="Tamanho">
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)} className={`h-10 min-w-10 rounded-lg px-3 text-sm font-bold ${size === s ? "bg-[#E4F222] text-black" : "border border-[#232B3E]"}`}>{s}</button>)}
                </div>
              </Field>
            )}
            <Field label="Quantidade">
              <div className="flex items-center gap-3">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="btn-ghost"><Minus className="h-4 w-4" /></button>
                <span data-testid="qty-value" className="w-6 text-center font-bold">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="btn-ghost"><Plus className="h-4 w-4" /></button>
              </div>
            </Field>
            {product.stock !== null && product.stock !== undefined && <p className="text-xs text-[#64748B]">{product.stock} em estoque</p>}
            <button data-testid="add-to-cart-btn" disabled={product.stock === 0} onClick={add} className="btn-lime flex w-full items-center justify-center gap-2 rounded-full py-3 disabled:opacity-50">
              <ShoppingCart className="h-4 w-4" />{product.stock === 0 ? "Esgotado" : "Adicionar ao carrinho"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PixPay = ({ order, onClose, onNotified }) => {
  const { settings } = useAuth();
  if (!order) return null;
  const key = settings?.pix_key || "circuitoaltax@gmail.com";
  const notify = async () => {
    try { await api.post(`/orders/${order.id}/status`, { status: "aguardando" }); toast.success("Pagamento informado! Aguarde a confirmação."); onNotified(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-[#232B3E] bg-[#121722] sm:max-w-md">
        <DialogHeader><DialogTitle className="h-display text-3xl">Pagar pedido #{order.number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-[#0B0E14] p-4 text-center">
            <div className="eyebrow">Total</div>
            <div data-testid="pix-order-total" className="font-display text-4xl font-bold text-[#E4F222]">{brl(order.total)}</div>
          </div>
          <div className="rounded-xl border border-[#E4F222]/50 p-4">
            <div className="eyebrow">Chave PIX (e-mail) · {settings?.pix_name}</div>
            <div data-testid="store-pix-key" className="mt-1 break-all font-display text-xl font-bold">{key}</div>
            <button data-testid="store-pix-copy" onClick={async () => { if (await copyText(key)) toast.success("Chave PIX copiada!"); }} className="btn-lime mt-3 flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm"><Copy className="h-4 w-4" />Copiar chave PIX</button>
          </div>
          <p className="text-xs text-[#94A3B8]">Abra o app do seu banco, escolha PIX copia e cola / chave e-mail, cole a chave e pague o valor total. Depois clique abaixo.</p>
          {order.status === "pendente" && <button data-testid="order-notify-paid" onClick={notify} className="btn-ghost flex w-full items-center justify-center gap-2"><Send className="h-4 w-4" />Já paguei</button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CartSheet = ({ open, onOpenChange, cart, setCart, onCheckout }) => {
  const total = cart.reduce((a, i) => a + i.price * i.qty, 0);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col border-[#232B3E] bg-[#121722]">
        <SheetHeader><SheetTitle className="h-display text-3xl">Carrinho</SheetTitle></SheetHeader>
        <div className="flex-1 space-y-2 overflow-y-auto py-4">
          {cart.length === 0 ? <p className="text-sm text-[#64748B]">Seu carrinho está vazio.</p> : cart.map((i, idx) => (
            <div key={idx} data-testid={`cart-item-${idx}`} className="flex items-center gap-3 rounded-xl border border-[#232B3E] p-2">
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#0B0E14]">{i.image && <img src={fileUrl(i.image)} alt="" className="h-full w-full object-cover" />}</div>
              <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{i.name}</div><div className="text-xs text-[#94A3B8]">{i.size && `Tam. ${i.size} · `}{i.qty}x {brl(i.price)}</div></div>
              <button data-testid={`cart-remove-${idx}`} onClick={() => setCart(cart.filter((_, j) => j !== idx))} className="p-1 text-[#64748B] hover:text-red-400"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-[#232B3E] pt-4">
          <div className="mb-3 flex justify-between font-semibold"><span>Total</span><span data-testid="cart-total" className="font-display text-2xl text-[#E4F222]">{brl(total)}</span></div>
          <button data-testid="checkout-btn" disabled={!cart.length} onClick={onCheckout} className="btn-lime w-full rounded-full py-3 disabled:opacity-50">Finalizar e pagar com PIX</button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const OrderRow = ({ o, isAdmin, onPay, onReload }) => {
  const setStatus = async (status) => { try { await api.post(`/orders/${o.id}/status`, { status }); toast.success("Pedido atualizado"); onReload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async () => { if (!window.confirm("Excluir pedido?")) return; await api.delete(`/orders/${o.id}`); onReload(); };
  const wa = isAdmin && waLink(o.user?.phone, `Olá ${firstName(o.user?.name)}! Sobre seu pedido #${o.number} na loja do Circuito Altax (${brl(o.total)}):`);
  return (
    <div data-testid={`order-${o.id}`} className="card-x p-4">
      <div className="flex flex-wrap items-center gap-3">
        {isAdmin && <Avatar user={o.user} size={38} />}
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Pedido #{o.number}{isAdmin && ` · ${o.user?.name || "Atleta removido"}`}</div>
          <div className="text-xs text-[#64748B]">{new Date(o.created_at).toLocaleString("pt-BR")}</div>
        </div>
        <div className="text-right"><div className="font-display text-xl font-bold">{brl(o.total)}</div><StatusPill testid={`order-status-${o.id}`} status={ORDER_PILL[o.status]} label={ORDER_STATUS[o.status]} /></div>
      </div>
      <div className="mt-3 space-y-1 text-sm text-[#94A3B8]">
        {o.items.map((i, k) => <div key={k}>{i.qty}x {i.name}{i.size && ` (${i.size})`} · {brl(i.price * i.qty)}</div>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {!isAdmin && o.status === "pendente" && <button data-testid={`order-pay-${o.id}`} onClick={() => onPay(o)} className="btn-lime rounded-full px-4 py-2 text-sm">Pagar com PIX</button>}
        {isAdmin && (
          <>
            <select data-testid={`order-status-select-${o.id}`} className="inp h-9 w-auto" value={o.status} onChange={(e) => setStatus(e.target.value)}>
              {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {wa && <a href={wa} target="_blank" rel="noreferrer" data-testid={`order-wa-${o.id}`} className="btn-ghost flex items-center gap-1"><MessageCircle className="h-4 w-4" />WhatsApp</a>}
            <button data-testid={`order-delete-${o.id}`} onClick={del} className="btn-ghost text-red-400"><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
    </div>
  );
};

export default function Store() {
  const { user, isAdmin } = useAuth();
  const cartKey = `altax_cart_${user.user_id}`;
  const [tab, setTab] = useState("produtos");
  const [cat, setCat] = useState("");
  const [products, setProducts] = useState(null);
  const [orders, setOrders] = useState(null);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ open: false, product: null });
  const [cartOpen, setCartOpen] = useState(false);
  const [payOrder, setPayOrder] = useState(null);
  const [cart, setCartState] = useState(() => { try { return JSON.parse(localStorage.getItem(cartKey)) || []; } catch { return []; } });
  const setCart = (c) => { setCartState(c); localStorage.setItem(cartKey, JSON.stringify(c)); };
  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { loadProducts(); loadOrders(); }, []);
  const shown = useMemo(() => (products || []).filter((p) => !cat || p.category === cat), [products, cat]);
  const checkout = async () => {
    try {
      const { data } = await api.post("/orders", { items: cart.map(({ product_id, size, qty }) => ({ product_id, size, qty })) });
      setCart([]); setCartOpen(false); setPayOrder(data); loadOrders(); loadProducts(); setTab("pedidos");
      toast.success("Pedido criado!");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delProduct = async (p) => { if (!window.confirm(`Excluir ${p.name}?`)) return; await api.delete(`/products/${p.id}`); loadProducts(); };
  const move = async (p, dir) => {
    const ids = products.map((x) => x.id);
    const i = ids.indexOf(p.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setProducts(ids.map((id) => products.find((x) => x.id === id)));
    await api.post("/products/reorder", { ids });
    toast.success("Ordem da vitrine atualizada");
  };
  const count = cart.reduce((a, i) => a + i.qty, 0);
  return (
    <div>
      <PageHeader eyebrow="Loja oficial" title="Loja" subtitle="Uniformes, bonés e pulseiras do circuito. Pagamento via PIX."
        action={
          <div className="flex gap-2">
            {isAdmin && <button data-testid="new-product-btn" onClick={() => setForm({ open: true, product: null })} className="btn-ghost flex items-center gap-1"><Plus className="h-4 w-4" />Produto</button>}
            <button data-testid="open-cart-btn" onClick={() => setCartOpen(true)} className="btn-lime relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm">
              <ShoppingCart className="h-4 w-4" />Carrinho{count > 0 && <span data-testid="cart-count" className="rounded-full bg-black px-1.5 text-xs text-[#E4F222]">{count}</span>}
            </button>
          </div>
        } />
      <div className="mb-5 flex gap-6 border-b border-[#232B3E]">
        {[["produtos", "Produtos"], ["pedidos", isAdmin ? "Pedidos" : "Meus pedidos"]].map(([v, l]) => (
          <button key={v} data-testid={`store-tab-${v}`} onClick={() => setTab(v)} className={`-mb-px border-b-2 pb-3 text-sm font-semibold ${tab === v ? "border-[#E4F222] text-white" : "border-transparent text-[#64748B]"}`}>{l}</button>
        ))}
      </div>
      {tab === "produtos" && (
        <>
          <PillTabs testid="store-cat" tabs={[{ value: "", label: "Todos" }, ...CATS]} value={cat} onChange={setCat} />
          {isAdmin && !cat && <p className="mt-2 text-xs text-[#64748B]">Use as setas nos produtos para definir a ordem na vitrine (login e início).</p>}
          <div className="mt-4">
            {!products ? <Loader /> : shown.length === 0 ? <Empty icon={ShoppingBag} title="Sem produtos" text={isAdmin ? "Cadastre o primeiro produto da loja." : "Em breve novidades na loja."} /> : (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {shown.map((p) => (
                  <div key={p.id} data-testid={`product-${p.id}`} className={`card-x card-hover overflow-hidden ${!p.active ? "opacity-50" : ""}`}>
                    <button onClick={() => setView(p)} data-testid={`product-open-${p.id}`} className="block w-full text-left">
                      <div className="aspect-square bg-[#0B0E14]">{p.images[0] ? <img src={fileUrl(p.images[0])} alt={p.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Package className="h-10 w-10 text-[#232B3E]" /></div>}</div>
                      <div className="p-3">
                        <div className="text-[10px] uppercase tracking-widest text-[#64748B]">{p.category}</div>
                        <div className="truncate font-semibold">{p.name}</div>
                        <div className="font-display text-xl font-bold text-[#E4F222]">{brl(p.price)}</div>
                        {p.stock === 0 && <div className="text-xs text-red-400">Esgotado</div>}
                      </div>
                    </button>
                    {isAdmin && (
                      <div className="flex border-t border-[#232B3E]">
                        {!cat && <button data-testid={`move-left-${p.id}`} title="Mover para antes" onClick={() => move(p, -1)} className="px-2 py-2 text-[#94A3B8] hover:text-[#E4F222]"><ChevronLeft className="h-4 w-4" /></button>}
                        <button data-testid={`edit-product-${p.id}`} onClick={() => setForm({ open: true, product: p })} className="flex flex-1 items-center justify-center gap-1 py-2 text-xs text-[#94A3B8] hover:text-white"><Pencil className="h-3.5 w-3.5" />Editar</button>
                        <button data-testid={`delete-product-${p.id}`} onClick={() => delProduct(p)} className="flex flex-1 items-center justify-center gap-1 py-2 text-xs text-[#94A3B8] hover:text-red-400"><Trash2 className="h-3.5 w-3.5" />Excluir</button>
                        {!cat && <button data-testid={`move-right-${p.id}`} title="Mover para depois" onClick={() => move(p, 1)} className="px-2 py-2 text-[#94A3B8] hover:text-[#E4F222]"><ChevronRight className="h-4 w-4" /></button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {tab === "pedidos" && (!orders ? <Loader /> : orders.length === 0 ? <Empty icon={Package} title="Nenhum pedido" /> : (
        <div className="space-y-2">{orders.map((o) => <OrderRow key={o.id} o={o} isAdmin={isAdmin} onPay={setPayOrder} onReload={loadOrders} />)}</div>
      ))}
      <ProductDialog product={view} onClose={() => setView(null)} onAdd={(item) => { setCart([...cart, item]); toast.success("Adicionado ao carrinho"); }} />
      <CartSheet open={cartOpen} onOpenChange={setCartOpen} cart={cart} setCart={setCart} onCheckout={checkout} />
      <PixPay order={payOrder} onClose={() => setPayOrder(null)} onNotified={loadOrders} />
      {isAdmin && <ProductForm open={form.open} product={form.product} onOpenChange={(o) => setForm({ ...form, open: o })} onSaved={loadProducts} />}
    </div>
  );
}
